import React, { useMemo, useState } from "react";
import {
  ChevronDownIcon,
  CheckCircleIcon,
  XCircleIcon,
  ServerStackIcon,
  CommandLineIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";

/**
 * Two independent things assert on a response, shown as separate groups because they answer
 * different questions and fail for different reasons:
 *
 *  - Runner assertions — the fixed checks the backend performs (status code, timing, ...).
 *  - Script assertions — ng.test() calls from the post-response script, where the generated
 *                        schema-aware per-field checks live.
 */

const SOURCE = {
  runner: {
    label: "Runner assertions",
    hint: "Fixed checks performed by the backend on every request",
    Icon: ServerStackIcon,
  },
  script: {
    label: "Script assertions",
    hint: "ng.test() cases from this request's post-response script",
    Icon: CommandLineIcon,
  },
};

/** Backend assertions arrive as { name: "PASS <detail>" | "FAIL <detail>" }. */
const normalizeRunnerResults = (raw) => {
  if (!raw || typeof raw !== "object") return [];
  return Object.entries(raw).map(([name, value]) => {
    const text = typeof value === "string" ? value : String(value ?? "");
    // startsWith, not includes — a detail mentioning a key such as PASSWORD must not flip a FAIL.
    const passed = text.startsWith("PASS");
    return {
      name,
      passed,
      detail: text.replace(/^(PASS|FAIL)\s*/, "").trim(),
    };
  });
};

/** Script assertions arrive as [{ name, status: "pass" | "fail", error }]. */
const normalizeScriptResults = (result) => {
  if (!result || !Array.isArray(result.testResults)) return [];
  return result.testResults.map((t) => ({
    name: t.name,
    passed: t.status === "pass",
    detail: t.status === "pass" ? "" : t.error || "Assertion failed",
  }));
};

const Pill = ({ tone, children, title }) => {
  const tones = {
    pass: "bg-green-50 text-green-700 border-green-200",
    fail: "bg-red-50 text-red-700 border-red-200",
    neutral: "bg-gray-50 text-gray-600 border-gray-200",
    warn: "bg-amber-50 text-amber-800 border-amber-200",
  };
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[11px] font-semibold tabular-nums ${tones[tone]}`}
    >
      {children}
    </span>
  );
};

const statusTone = (status) => {
  if (!status) return "neutral";
  if (status >= 200 && status < 300) return "pass";
  if (status >= 300 && status < 400) return "warn";
  return "fail";
};

const formatBytes = (n) => {
  if (!n && n !== 0) return null;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

const AssertionRow = ({ name, passed, detail }) => (
  <div className="flex items-start gap-2 px-3 py-2 border-b border-gray-100 last:border-b-0 hover:bg-gray-50">
    {passed ? (
      <CheckCircleIcon className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
    ) : (
      <XCircleIcon className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
    )}
    <div className="min-w-0 flex-1">
      <div className="text-[13px] font-medium text-gray-800 break-words">{name}</div>
      {detail && (
        <div className={`text-xs mt-0.5 break-words ${passed ? "text-gray-500" : "text-red-700"}`}>
          {detail}
        </div>
      )}
    </div>
    <span
      className={`text-[10px] font-bold tracking-wide flex-shrink-0 mt-0.5 ${
        passed ? "text-green-700" : "text-red-700"
      }`}
    >
      {passed ? "PASS" : "FAIL"}
    </span>
  </div>
);

const AssertionGroup = ({ source, rows, emptyNote, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  const { label, hint, Icon } = SOURCE[source];
  const failed = rows.filter((r) => !r.passed).length;
  const passed = rows.length - failed;

  return (
    <div className="border border-gray-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="w-full flex items-center gap-2 px-3 py-2.5 bg-gray-50 border-b border-gray-200 text-left hover:bg-gray-100 cursor-pointer"
      >
        <ChevronDownIcon
          className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${
            open ? "" : "-rotate-90"
          }`}
        />
        <Icon className="h-4 w-4 text-gray-500 flex-shrink-0" />
        <span className="text-[13px] font-semibold text-gray-800">{label}</span>
        <span className="text-[11px] text-gray-400 truncate hidden sm:inline">{hint}</span>
        <span className="ml-auto flex items-center gap-1.5 flex-shrink-0">
          {rows.length === 0 ? (
            <Pill tone="neutral">none</Pill>
          ) : (
            <>
              {failed > 0 && <Pill tone="fail">{failed} failed</Pill>}
              <Pill tone={failed === 0 ? "pass" : "neutral"}>
                {passed}/{rows.length} passed
              </Pill>
            </>
          )}
        </span>
      </button>

      {open && (
        <div>
          {rows.length === 0 ? (
            <div className="px-3 py-3 text-xs text-gray-500">{emptyNote}</div>
          ) : (
            // Failures first — the reason anyone opens this panel.
            [...rows]
              .sort((a, b) => Number(a.passed) - Number(b.passed))
              .map((r, i) => <AssertionRow key={`${r.name}-${i}`} {...r} />)
          )}
        </div>
      )}
    </div>
  );
};

const TestResults = ({
  testResults,
  scriptResults,
  hasPostResponseScript = false,
  hasPreRequestScript = false,
}) => {
  const runnerRows = useMemo(() => normalizeRunnerResults(testResults?.testResults), [testResults]);

  const postScript = scriptResults?.postResponse;
  const preScript = scriptResults?.preRequest;

  const scriptRows = useMemo(
    () => [...normalizeScriptResults(preScript), ...normalizeScriptResults(postScript)],
    [preScript, postScript],
  );

  const scriptCrashed = [preScript, postScript].filter((r) => r && r.success === false);

  if (!testResults || Object.keys(testResults).length === 0) {
    return (
      <div className="p-6 text-sm text-gray-500 font-apple">
        No results yet — send this request to see assertion results.
      </div>
    );
  }

  const status = testResults.status ?? testResults.originalResponse?.status;
  const statusText = testResults.statusText;
  const time = testResults.responseTime;
  const size = formatBytes(testResults.responseSize);
  const contentType = testResults.contentType;

  const totalFailed =
    runnerRows.filter((r) => !r.passed).length + scriptRows.filter((r) => !r.passed).length;
  const totalRows = runnerRows.length + scriptRows.length;

  const scriptEmptyNote =
    !hasPostResponseScript && !hasPreRequestScript
      ? "No script on this request. Add ng.test() cases in the Test Scripts tab to assert on the response body."
      : postScript || preScript
        ? "The script ran but declared no ng.test() cases."
        : "Script has not run yet for this response.";

  return (
    <div className="bg-white w-full min-h-full font-apple">
      {/* Response summary */}
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-sm font-semibold text-gray-900 mr-1">Test Results</h2>

          {status != null && (
            <Pill tone={statusTone(status)} title="HTTP status returned by the API">
              {status}
              {statusText ? ` ${statusText}` : ""}
            </Pill>
          )}
          {time != null && (
            <Pill tone="neutral" title="Round-trip time">
              {time} ms
            </Pill>
          )}
          {size && (
            <Pill tone="neutral" title="Response body size">
              {size}
            </Pill>
          )}
          {contentType && (
            <Pill tone="neutral" title="Response Content-Type">
              {String(contentType).split(";")[0]}
            </Pill>
          )}

          <span className="ml-auto flex items-center gap-1.5">
            {totalRows > 0 &&
              (totalFailed > 0 ? (
                <Pill tone="fail">
                  {totalFailed} of {totalRows} failed
                </Pill>
              ) : (
                <Pill tone="pass">all {totalRows} passed</Pill>
              ))}
          </span>
        </div>
      </div>

      <div className="p-3 space-y-3">
        {scriptCrashed.length > 0 && (
          <div className="flex items-start gap-2 border border-amber-200 bg-amber-50 px-3 py-2">
            <ExclamationTriangleIcon className="h-4 w-4 text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-amber-900 min-w-0">
              <div className="font-semibold">Script did not finish</div>
              {scriptCrashed.map((r, i) => (
                <div key={i} className="font-mono break-words mt-0.5">
                  {r.error || "Unknown script error"}
                </div>
              ))}
              <div className="mt-1 text-amber-800">
                Any assertions after the error did not run, so this list is incomplete.
              </div>
            </div>
          </div>
        )}

        <AssertionGroup
          source="runner"
          rows={runnerRows}
          emptyNote="The runner produced no assertions for this response."
        />

        <AssertionGroup source="script" rows={scriptRows} emptyNote={scriptEmptyNote} />

        {/* Response body, last — it is reference material, not a result */}
        {testResults.originalResponse !== undefined && (
          <details className="border border-gray-200 bg-white">
            <summary className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 border-b border-gray-200 cursor-pointer list-none hover:bg-gray-100 select-none">
              <ChevronDownIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <span className="text-[13px] font-semibold text-gray-800">Response body</span>
              <span className="text-[11px] text-gray-400">for reference</span>
            </summary>
            <pre className="p-3 text-xs font-mono text-gray-800 overflow-x-auto max-h-96 overflow-y-auto">
              {JSON.stringify(testResults.originalResponse, null, 2)}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
};

export default TestResults;
